import React, { useCallback } from 'react';
import InputNumberFormat from 'react-number-format';

// import { Warning as WarningIcon } from '@styled-icons/entypo/Warning';
import AlphaIBoxOutlineIcon from 'mdi-react/AlphaIBoxOutlineIcon';
import MdiGroupIcon from 'mdi-react/GroupIcon';

import {
  Box,
  Icon,
  Tr,
  Td,
  Text,
  Flex,
  // Tooltip,
  Input,
} from '@chakra-ui/react';

import { Row, FlattenedComponent } from './types';

export type Composition = {
  price: number;
  unit_price_was_changed: boolean;
  unit_price_original: number;
  newQuantity: number;
  recursive_quantity: number;
  outdated_price: boolean;
  bdi_percent: number;
  composition_price: {
    custom_description: string | null;
    base: {
      description: string;
    };
    version: {
      description: string;
    };
    composition: {
      code: string;
      description: string;
      kind: 'informed' | 'common';
      version: {
        year?: number;
        month?: number;
        description: string;
        base: {
          description: string;
        };
      };
      unit_measure: {
        description: string;
      };
    };
  };
};

type Item = FlattenedComponent & Composition;

interface RowProps {
  row: Row & { original: Item };
  data: FlattenedComponent[];
  onUpdateComponentBDI: (id: number, bdi: number | undefined) => void;
}

const CompositionRow: React.FC<RowProps> = ({
  row,
  data,
  onUpdateComponentBDI,
}) => {
  const itemAppearsInMoreThanOneLocation = useCallback(
    (item: Item) => {
      return (
        data.filter(
          (i) =>
            i.composition_price?.composition.code ===
            item.composition_price.composition.code,
        ).length > 1
      );
    },
    [data],
  );

  const getAllLocations = useCallback(
    (item: Item) => {
      return data
        .filter(
          (i) =>
            i.composition_price?.composition.code ===
            item.composition_price.composition.code,
        )
        .map((i) => i.location);
    },
    [data],
  );

  return (
    <Tr {...row.getRowProps()}>
      <Td>
        <Box
          display="flex"
          flexDir="column"
          ml="1"
          justifyContent="center"
          alignItems="center"
          fontWeight="bold"
        >
          {row.original.location}
          {row.original.composition_price.composition.kind === 'informed' ? (
            <Icon w={5} h={5} as={AlphaIBoxOutlineIcon} />
          ) : (
            <Icon w={5} h={5} as={MdiGroupIcon} />
          )}
        </Box>
      </Td>

      <Td title="Base / Referência">
        <Text textAlign="center">
          {row.original.composition_price.base?.description}
        </Text>

        <Text
          textAlign="center"
          whiteSpace="pre-wrap"
          maxWidth={{ base: '100%', md: '650px' }}
        >
          {row.original.composition_price.version?.description}
        </Text>
      </Td>

      <Td>{row.original.composition_price.composition.code}</Td>

      <Td>
        <Text
          py={1}
          whiteSpace="pre-wrap"
          maxWidth={{ base: '100%', md: '650px' }}
        >
          {row.original.composition_price.custom_description ||
            row.original.composition_price.composition.description}
        </Text>

        {itemAppearsInMoreThanOneLocation(row.original) && (
          <Text
            fontSize="xs"
            fontWeight="bold"
            color="gray.700"
            whiteSpace="pre-wrap"
            maxWidth={{ base: '100%', md: '650px' }}
          >
            Esse serviço está presente nas posições:{' '}
            {getAllLocations(row.original).join(', ')} do orçamento
          </Text>
        )}
      </Td>

      <Td isNumeric>
        {Intl.NumberFormat('pt-BR', {
          maximumFractionDigits: 4,
        }).format(row.original.quantity)}
      </Td>

      <Td isNumeric>
        <Flex gap={2} alignItems="center" justifyContent="flex-end">
          <Text>
            {Intl.NumberFormat('pt-BR', {
              style: 'currency',
              currency: 'BRL',
            }).format(row.original.social_law_price)}
          </Text>

          {/* {row.original.outdated_price && (
            <Tooltip
              ml={1}
              hasArrow
              label="Serviço com preço desatualizado em relação a sua versão original."
            >
              <Icon as={WarningIcon} className="icon" />
            </Tooltip>
          )} */}
        </Flex>
      </Td>

      <Td isNumeric>
        {Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL',
        }).format(row.original.price)}
      </Td>

      <Td isNumeric>
        <InputNumberFormat
          type="tel"
          customInput={Input}
          displayType="input"
          className="text-right"
          placeholder="0 %"
          fixedDecimalScale
          thousandSeparator="."
          decimalSeparator=","
          suffix=" %"
          decimalScale={2}
          value={row.original.bdi_percent}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              const currentElement = e.target as HTMLInputElement;

              const inputs = document.querySelectorAll('input');
              const currentIndex = Array.from(inputs).indexOf(currentElement);

              if (currentIndex !== -1 && currentIndex < inputs.length - 1) {
                const nextElement = inputs[currentIndex + 1];
                nextElement.focus();
              }
            }
          }}
          onFocus={(e) => e.target.select()}
          onValueChange={(v) => {
            onUpdateComponentBDI(row.original.id, v.floatValue);
          }}
        />
      </Td>

      <Td isNumeric>
        {Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL',
        }).format(row.original.total)}
      </Td>
    </Tr>
  );
};

export default CompositionRow;
